import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clinicAPI from "../../../services/clinicApi";
import {
  activationSuccessMessage,
  activationWarningMessage,
} from "../../../utils/membershipActivationMessage";
import { membershipEffectivePrice } from "../AdminPatients/clientSportDisplay";
import s from "../clinic.module.css";
import { Button, Input, Select, useToast } from "../../ui";
import { toDateInputValue } from "../../../utils/appointmentLabels";

function dueAmount(membership) {
  if (membership?.amount_due != null && membership.amount_due !== "") {
    const n = Number(membership.amount_due);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  const paid = Number(membership?.paid_total || 0);
  const required = membershipEffectivePrice(membership);
  return Math.max(0, required - (Number.isFinite(paid) ? paid : 0));
}

function dueAmountString(membership) {
  const n = dueAmount(membership);
  return Number.isFinite(n) ? String(Math.round(n)) : "";
}

function fmtRub(n) {
  return `${Math.round(Number(n) || 0).toLocaleString("ru-RU")} ₽`;
}

export function MoreMenu({ items, size = "sm", disabled }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  const visible = (items || []).filter(Boolean);

  useLayoutEffect(() => {
    if (!open || !wrapRef.current) {
      setCoords(null);
      return undefined;
    }
    const place = () => {
      const rect = wrapRef.current.getBoundingClientRect();
      const panelH = panelRef.current?.offsetHeight || 220;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < panelH + 12 && rect.top > spaceBelow;
      const width = Math.max(220, rect.width);
      setCoords({
        top: openUp ? undefined : rect.bottom + 4,
        bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        left: Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8),
        minWidth: width,
      });
    };
    place();
    // второй проход после измерения высоты панели
    requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, visible.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (
        wrapRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!visible.length) return null;

  const panel = open
    ? createPortal(
        <div
          ref={panelRef}
          className={s.membershipMoreMenuPanel}
          role="menu"
          style={
            coords
              ? {
                  position: "fixed",
                  top: coords.top,
                  bottom: coords.bottom,
                  left: coords.left,
                  minWidth: coords.minWidth,
                  zIndex: 10050,
                }
              : {
                  position: "fixed",
                  top: 0,
                  left: 0,
                  visibility: "hidden",
                  zIndex: 10050,
                }
          }
        >
          {visible.map((item) => (
            <button
              key={item.id || item.label}
              type="button"
              role="menuitem"
              className={s.membershipMoreMenuItem}
              disabled={item.disabled || disabled}
              onClick={() => {
                setOpen(false);
                item.onClick?.();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={s.membershipMoreMenu} ref={wrapRef}>
      <Button
        type="button"
        size={size}
        variant="secondary"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Ещё
      </Button>
      {panel}
    </div>
  );
}

export default function AcceptMembershipPayment({
  membership,
  patientId,
  onSuccess,
  onOpenChange,
  size = "sm",
  /** Доп. пункты в меню «Ещё» (Изменить, Заморозка и т.п.) */
  extraActions = [],
}) {
  const toast = useToast();
  const [mode, setMode] = useState("idle"); // idle | pay | editDebt
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("cash");
  const due = dueAmount(membership);
  const paidTotal = Number(membership?.paid_total || 0);
  const isFree = membershipEffectivePrice(membership) <= 0 && due <= 0;
  const isPending = membership?.status === "pending";
  const isActiveDebt =
    due > 0 &&
    (membership?.status === "active" ||
      membership?.status === "frozen" ||
      membership?.status === "expired");
  const [amount, setAmount] = useState(dueAmountString(membership));
  const [debtEdit, setDebtEdit] = useState(dueAmountString(membership));
  const [paidDate, setPaidDate] = useState(() => toDateInputValue());

  useEffect(() => {
    if (mode === "pay") {
      setAmount(dueAmountString(membership));
      setPaidDate(toDateInputValue());
    }
    if (mode === "editDebt") setDebtEdit(dueAmountString(membership));
  }, [membership?.id, membership?.amount_due, membership?.price, mode]);

  const setFormOpen = (next) => {
    setMode(next ? "pay" : "idle");
    onOpenChange?.(Boolean(next));
  };

  if (!membership) return null;
  if (!isPending && !isActiveDebt && !isFree && !extraActions?.length) {
    return null;
  }
  if (!isPending && due <= 0 && !isFree && !extraActions?.length) {
    return null;
  }

  const canCancelIssue =
    isPending || (isActiveDebt && paidTotal <= 0);

  const cancelIssue = async () => {
    if (
      !window.confirm(
        isPending
          ? "Отменить неоплаченный абонемент? Клиент сможет оформить заказ заново."
          : "Отменить выдачу? Абонемент перестанет действовать, долг исчезнет.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await clinicAPI.cancelClientMembership(membership.id);
      if (res.status === "confirmed") {
        toast.success(
          res.message || "Оплата уже прошла — абонемент активирован",
        );
      } else {
        toast.success("Абонемент отменён");
      }
      onSuccess?.(res);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось отменить");
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const sum = Number(String(amount).replace(",", "."));
    if (!Number.isFinite(sum) || sum <= 0) {
      toast.error("Укажите сумму");
      return;
    }
    if (!paidDate) {
      toast.error("Укажите дату оплаты");
      return;
    }
    const paidAt = new Date(`${paidDate}T12:00:00`);
    if (Number.isNaN(paidAt.getTime())) {
      toast.error("Некорректная дата оплаты");
      return;
    }
    setBusy(true);
    try {
      const data = await clinicAPI.createMembershipPayment({
        patient_id: membership.patient_id || patientId,
        membership_id: membership.id,
        amount: sum,
        method,
        paid_at: paidAt.toISOString(),
      });
      toast.success(activationSuccessMessage(data.activation));
      const warn = activationWarningMessage(data.activation);
      if (warn) toast.warning(warn);
      setFormOpen(false);
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось записать оплату");
    } finally {
      setBusy(false);
    }
  };

  const activateCovered = async () => {
    if (
      !window.confirm(
        "Активировать без оплаты? В финансы сумма не попадёт.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const data = await clinicAPI.activateCoveredMembership(membership.id);
      toast.success(activationSuccessMessage(data.activation));
      const warn = activationWarningMessage(data.activation);
      if (warn) toast.warning(warn);
      setFormOpen(false);
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось активировать");
    } finally {
      setBusy(false);
    }
  };

  const forgiveDebt = async () => {
    if (
      !window.confirm(
        paidTotal > 0
          ? `Списать остаток долга? Сумма к оплате станет ${fmtRub(paidTotal)} (уже внесено).`
          : "Списать долг без оплаты? Сумма к оплате станет 0 ₽.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const data = await clinicAPI.updateClientMembership(membership.id, {
        price: paidTotal,
      });
      toast.success("Долг списан");
      setMode("idle");
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось списать долг");
    } finally {
      setBusy(false);
    }
  };

  const saveDebtEdit = async (e) => {
    e.preventDefault();
    const newDue = Number(String(debtEdit).replace(",", "."));
    if (!Number.isFinite(newDue) || newDue < 0) {
      toast.error("Укажите сумму долга от 0");
      return;
    }
    const nextPrice = Math.round((paidTotal + newDue) * 100) / 100;
    setBusy(true);
    try {
      const data = await clinicAPI.updateClientMembership(membership.id, {
        price: nextPrice,
      });
      toast.success(
        newDue > 0 ? `Долг обновлён: ${fmtRub(newDue)}` : "Долг обнулён",
      );
      setMode("idle");
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось изменить долг");
    } finally {
      setBusy(false);
    }
  };

  const activateFree = async () => {
    setBusy(true);
    try {
      const data = await clinicAPI.createMembershipPayment({
        patient_id: patientId || membership.patient_id,
        membership_id: membership.id,
        amount: 0,
        method: "other",
        paid_at: new Date().toISOString(),
      });
      toast.success(activationSuccessMessage(data.activation));
      const warn = activationWarningMessage(data.activation);
      if (warn) toast.warning(warn);
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось активировать");
    } finally {
      setBusy(false);
    }
  };

  const debtMenuItems = isActiveDebt
    ? [
        {
          id: "edit-debt",
          label: "Изменить сумму долга",
          onClick: () => {
            setDebtEdit(dueAmountString(membership));
            setMode("editDebt");
          },
        },
        {
          id: "forgive",
          label: "Списать долг",
          onClick: forgiveDebt,
        },
      ]
    : [];

  const pendingMenuItems = isPending
    ? [
        {
          id: "free",
          label: "Без оплаты",
          onClick: activateCovered,
        },
      ]
    : [];

  const cancelMenuItem = canCancelIssue
    ? {
        id: "cancel",
        label: "Отменить выдачу",
        onClick: cancelIssue,
      }
    : null;

  const moreItems = [
    ...debtMenuItems,
    ...pendingMenuItems,
    ...(extraActions || []),
    cancelMenuItem,
  ];

  if (isPending && isFree) {
    return (
      <div className={s.membershipActionBar}>
        <Button type="button" size={size} disabled={busy} onClick={activateFree}>
          {busy ? "…" : "Активировать"}
        </Button>
        <MoreMenu items={moreItems} size={size} disabled={busy} />
      </div>
    );
  }

  if (mode === "editDebt" && isActiveDebt) {
    return (
      <form className={s.membershipDebtBox} onSubmit={saveDebtEdit}>
        <p className={s.membershipDebtBoxTitle}>Сумма долга</p>
        <p className={s.membershipDebtBoxHint}>
          Уже внесено {fmtRub(paidTotal)}. Укажите, сколько ещё должны.
        </p>
        <label className={s.membershipPayField}>
          <span className={s.membershipPayLabel}>Долг, ₽</span>
          <Input
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={debtEdit}
            onChange={(e) => setDebtEdit(e.target.value)}
            disabled={busy}
          />
        </label>
        <div className={s.membershipPayFormBtns}>
          <Button type="submit" size={size} disabled={busy}>
            {busy ? "…" : "Сохранить"}
          </Button>
          <Button
            type="button"
            size={size}
            variant="secondary"
            disabled={busy}
            onClick={() => setMode("idle")}
          >
            Назад
          </Button>
        </div>
      </form>
    );
  }

  if (mode === "pay") {
    return (
      <form className={s.membershipDebtBox} onSubmit={submit}>
        <p className={s.membershipDebtBoxTitle}>
          {isActiveDebt ? `К оплате ${fmtRub(due)}` : "Записать оплату"}
        </p>
        <p className={s.membershipDebtBoxHint}>
          Можно принять меньше — остаток останется долгом.
        </p>
        <label className={s.membershipPayField}>
          <span className={s.membershipPayLabel}>Принимаете сейчас, ₽</span>
          <Input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={busy}
          />
        </label>
        <label className={s.membershipPayField}>
          <span className={s.membershipPayLabel}>Способ</span>
          <Select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            disabled={busy}
          >
            <option value="cash">Наличные</option>
            <option value="card_transfer">Перевод</option>
            <option value="other">Другое</option>
          </Select>
        </label>
        <label className={s.membershipPayField}>
          <span className={s.membershipPayLabel}>Дата оплаты</span>
          <Input
            type="date"
            value={paidDate}
            max={toDateInputValue()}
            onChange={(e) => setPaidDate(e.target.value)}
            disabled={busy}
            required
          />
        </label>
        <div className={s.membershipPayFormBtns}>
          <Button type="submit" size={size} disabled={busy}>
            {busy ? "…" : "Принять оплату"}
          </Button>
          <Button
            type="button"
            size={size}
            variant="secondary"
            disabled={busy}
            onClick={() => setFormOpen(false)}
          >
            Назад
          </Button>
        </div>
      </form>
    );
  }

  if (isActiveDebt) {
    return (
      <div className={s.membershipDebtBox}>
        <div className={s.membershipDebtBoxHead}>
          <strong className={s.membershipDebtBoxSum}>Долг {fmtRub(due)}</strong>
          <span className={s.membershipDebtBoxHint}>
            Можно принять часть суммы
          </span>
        </div>
        <div className={s.membershipActionBar}>
          <Button
            type="button"
            size={size}
            onClick={() => {
              setAmount(dueAmountString(membership));
              setFormOpen(true);
            }}
          >
            Принять оплату
          </Button>
          <MoreMenu items={moreItems} size={size} disabled={busy} />
        </div>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className={s.membershipActionBar}>
        <Button
          type="button"
          size={size}
          onClick={() => {
            setAmount(dueAmountString(membership));
            setFormOpen(true);
          }}
        >
          Записать оплату
        </Button>
        <MoreMenu items={moreItems} size={size} disabled={busy} />
      </div>
    );
  }

  // Нет долга — основная «Изменить», остальное в «Ещё»
  if (extraActions?.length) {
    const primary =
      extraActions.find((a) => a.id === "adjust") || extraActions[0];
    const rest = extraActions.filter((a) => a !== primary);
    return (
      <div className={s.membershipActionBar}>
        {primary ? (
          <Button
            type="button"
            size={size}
            variant="secondary"
            disabled={busy}
            onClick={primary.onClick}
          >
            {primary.id === "adjust" ? "Изменить" : primary.label}
          </Button>
        ) : null}
        <MoreMenu items={rest} size={size} disabled={busy} />
      </div>
    );
  }

  return null;
}
